import { useMemo } from 'react'

import { useFormContext } from 'react-hook-form'

import {
  Event,
  RecurringEventUpdateMode,
} from '@tribeplatform/gql-client/types'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Divider } from '@tribeplatform/react-ui-kit/Divider'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Text } from '@tribeplatform/react-ui-kit/Text'

import { FormMethods } from '../../Form/index.js'
import { T } from '../../i18n/index.js'
import { isRecurringEvent } from '../EventDetails/utils.js'
import { EventFormFields } from '../types.js'

interface Props {
  onSubmit: (
    data: EventFormFields,
    methods: FormMethods<EventFormFields>,
    recurrenceConfig?: RecurringEventUpdateMode,
  ) => Promise<unknown>
  onCancel?: () => void
  event?: Event
  isDirty: boolean
  loading: boolean
}

export const EventFormActionButtons = ({
  onSubmit,
  onCancel,
  event,
  isDirty,
  loading,
}: Props) => {
  const formCtx = useFormContext()
  const isRecurring = isRecurringEvent(event)

  /**
   * The time and recurrence config cannot be updated for only one event in the recurrence series.
   */
  const cannotUpdateOnlyThisEvent = useMemo(() => {
    const { dirtyFields } = formCtx.formState
    return isRecurring && !!dirtyFields?.recurrenceConfig
  }, [formCtx.formState, isRecurring])

  return (
    <div className="flex gap-3 w-full justify-end items-center flex-wrap">
      <div className="flex gap-2">
        <Button size="lg" variant="secondaryNeutral" onClick={onCancel}>
          <T id="Generics.Cancel" defaultMessage="Cancel" />
        </Button>
        <div className="flex">
          {!isRecurring && (
            <Button
              onClick={formCtx.handleSubmit((data: EventFormFields) =>
                onSubmit(data, formCtx),
              )}
              loading={loading}
            >
              {event?.id ? (
                <T id="Generics.Update" defaultMessage="Update" />
              ) : (
                <T id="Generics.Publish" defaultMessage="Publish" />
              )}
            </Button>
          )}
          {event?.id && isRecurring && (
            <Dropdown placement="top-end">
              <Dropdown.ButtonMinimal as="div" disabled={!isDirty || loading}>
                <Button loading={loading}>
                  <T id="Generics.Update" defaultMessage="Update" />
                </Button>
              </Dropdown.ButtonMinimal>
              <Dropdown.Items>
                <div className="flex flex-col gap-2 p-2">
                  <div className="flex items-center gap-2">
                    <SvgIcon name="calendar" />
                    <Text className="font-medium">
                      <T
                        id="Event.Form.RecurringEvent.Update.Title"
                        defaultMessage="Update recurring event"
                      />
                    </Text>
                  </div>
                  <Text className="text-content-muted">
                    <T
                      id="Event.Form.RecurringEvent.Update.Description"
                      defaultMessage="Which events would you like to update?"
                    />
                  </Text>
                  <Divider padding="sm" />
                </div>
                {!cannotUpdateOnlyThisEvent && (
                  <Dropdown.Item
                    disabled={loading}
                    leadingIcon={
                      <SvgIcon
                        name="calendar"
                        className="text-content-subdued"
                      />
                    }
                    onClick={formCtx.handleSubmit((data: EventFormFields) =>
                      onSubmit(
                        data,
                        formCtx,
                        RecurringEventUpdateMode.thisEventOnly,
                      ),
                    )}
                    className="!items-start"
                  >
                    <Text className="font-medium">
                      <T
                        id="Event.RecurringEvent.Actions.ThisEvent"
                        defaultMessage="Only this event"
                      />
                    </Text>
                  </Dropdown.Item>
                )}
                <Dropdown.Item
                  disabled={loading}
                  leadingIcon={
                    <SvgIcon name="calendar" className="text-content-subdued" />
                  }
                  onClick={formCtx.handleSubmit((data: EventFormFields) =>
                    onSubmit(
                      data,
                      formCtx,
                      RecurringEventUpdateMode.thisAndFutureEvents,
                    ),
                  )}
                  className="!items-start"
                >
                  <Text className="font-medium">
                    <T
                      id="Event.RecurringEvent.Actions.ThisAndFollowingEvents"
                      defaultMessage="This and following events"
                    />
                  </Text>
                </Dropdown.Item>
                <Dropdown.Item
                  disabled={loading}
                  leadingIcon={
                    <SvgIcon name="refresh" className="text-content-subdued" />
                  }
                  onClick={formCtx.handleSubmit((data: EventFormFields) =>
                    onSubmit(data, formCtx, RecurringEventUpdateMode.allEvents),
                  )}
                  className="!items-start"
                >
                  <Text className="font-medium">
                    <T
                      id="Event.RecurringEvent.Actions.AllEvents"
                      defaultMessage="All events in the series"
                    />
                  </Text>
                </Dropdown.Item>
              </Dropdown.Items>
            </Dropdown>
          )}
        </div>
      </div>
    </div>
  )
}
